"use client";

import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  Form,
  FormField,
  FormItem,
  Select,
  SelectContent,
  SelectTrigger,
  SelectValue,
  SelectItem,
  ScrollArea,
  DialogTitle,
} from "@reactive-resume/ui";
import { useDialog } from "@/client/stores/dialog";
import { useTranslation } from "@/client/services/ollama/traduction-ai";
import { languageTrans } from "@reactive-resume/utils";
import { FormProvider, useForm } from "react-hook-form";
import { XIcon, GlobeIcon, CheckIcon, SparkleIcon } from "@phosphor-icons/react";
import { useResumeStore } from "@/client/stores/resume";
import { useEffect } from "react";

const defaultValues = { language: "anglais" };
type FormValue = { language: string };

export const DialogTranslationI = () => {
  const { isOpen, close } = useDialog("translate");
  const translation = useTranslation();
  const resume = useResumeStore((state) => state.resume);
  const setResume = useResumeStore((state) => state.setValue);
  const form = useForm<FormValue>({ defaultValues });

  const onSubmitLanguage = (data: FormValue) => {
    translation.mutate({
      resume: resume.data,
      language: data.language,
    } as any);
  };

  useEffect(() => {
    if (translation.data) {
      setResume("basics", translation.data?.basics);
      setResume("metadata", translation.data?.metadata);
      setResume("sections", translation.data?.sections);
    }
  }, [translation]);

  return (
    <Dialog open={isOpen} onOpenChange={close}>
      <DialogContent className="max-w-[520px]">
        <DialogHeader className="pb-2">
          {/* Titre et Description intégrés dans le style visuel de la carte */}
          <div className="flex items-start gap-4">
            <div className="flex size-12 shrink-0 items-center justify-center rounded-full bg-emerald-100/60 text-emerald-600">
              <GlobeIcon className="size-6" />
            </div>
            <div>
              <DialogTitle className="mb-1 text-lg">Traduction IA</DialogTitle>
              <div className="text-sm text-text-muted">
                Traduisez instantanément votre CV dans plus de 8 langues.
              </div>
            </div>
          </div>
        </DialogHeader>

        <ScrollArea className="max-h-[60vh] px-1 pr-2">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmitLanguage)} className="space-y-5 pt-2">
              {/* Sélecteur de langue */}
              <FormField
                name="language"
                control={form.control}
                render={({ field }) => (
                  <FormItem>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <SelectTrigger className="bg-input-default h-11 border border-border-input hover:bg-surface-hover">
                        <SelectValue placeholder="Choisissez votre langue" />
                      </SelectTrigger>
                      <SelectContent>
                        <ScrollArea orientation="vertical" className="h-[200px]">
                          {languageTrans.map((item) => (
                            <SelectItem key={item.key} value={item.key}>
                              {item.value}
                            </SelectItem>
                          ))}
                        </ScrollArea>
                      </SelectContent>
                    </Select>
                  </FormItem>
                )}
              />

              {/* Badge de confirmation (Conserve le style de la capture d'écran) */}
              <div className="flex items-start gap-2 rounded-lg border border-status-success-border bg-status-success-subtle p-3">
                <CheckIcon
                  className="text-icon-success-default mt-0.5 size-5 shrink-0"
                  weight="bold"
                />
                <div className="text-sm leading-tight text-status-success-foreground">
                  <span className="block font-medium">Traduction professionnelle</span>
                  Conservez le format et la mise en page de votre CV.
                </div>
              </div>

              {/* Bouton d'action principal */}
              <div className="mt-4 flex flex-col gap-y-2">
                <Button
                  type="submit"
                  disabled={translation.isPending}
                  className="flex h-11 w-full items-center justify-center gap-2 rounded-lg bg-action-primary font-medium text-action-primary-foreground hover:bg-action-primary-hover"
                >
                  {translation.isPending ? (
                    <>
                      <div className="size-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                      Traduction en cours...
                    </>
                  ) : (
                    <>
                      <SparkleIcon className="size-5" />
                      Traduire mon CV
                    </>
                  )}
                </Button>
              </div>
            </form>
          </Form>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};
